import { useEffect, useState } from "react";
import { CalendarPlus, MapPin } from "lucide-react";
import { SUMMIT } from "@/constants/summit";
import { Reveal } from "@/components/Reveal";

const useCountdown = (target) => {
  const calc = () => {
    const diff = Math.max(0, new Date(target).getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const i = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return t;
};

const pad = (n) => String(n).padStart(2, "0");

const Countdown = () => {
  const t = useCountdown(SUMMIT.dateISO);
  const units = [
    { label: "Days", value: t.days, testId: "countdown-timer-days" },
    { label: "Hours", value: t.hours, testId: "countdown-timer-hours" },
    { label: "Minutes", value: t.minutes, testId: "countdown-timer-minutes" },
    { label: "Seconds", value: t.seconds, testId: "countdown-timer-seconds" },
  ];

  return (
    <section id="countdown" data-testid="countdown-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,87,34,0.10),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-400 mb-4">The Countdown Has Begun</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Doors open in
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 max-w-4xl mx-auto">
            {units.map((u) => (
              <div
                key={u.label}
                data-testid={u.testId}
                className="relative rounded-2xl border border-white/10 bg-charcoal/80 px-4 py-8 sm:py-10 text-center overflow-hidden"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/70 to-transparent" />
                <span className="font-mono text-5xl sm:text-6xl lg:text-7xl font-bold tabular-nums tracking-tight text-white">
                  {pad(u.value)}
                </span>
                <span className="mt-3 block font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-400">
                  {u.label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 mx-auto max-w-4xl rounded-2xl border border-white/10 bg-charcoal/60 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-500/15 border border-brand-500/30">
                <MapPin className="h-5 w-5 text-brand-400" />
              </span>
              <div>
                <p data-testid="countdown-venue-name" className="font-display text-xl font-semibold">{SUMMIT.venue}</p>
                <p className="text-sm text-white/55">{SUMMIT.address}, {SUMMIT.city}</p>
                <p data-testid="countdown-date-label" className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-brand-300">
                  {SUMMIT.dateLabel} · {SUMMIT.timeLabel}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                  {SUMMIT.programLabel}
                </p>
              </div>
            </div>
            <a
              data-testid="countdown-add-calendar-link"
              href={SUMMIT.calendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-brand-500/50 px-5 py-2.5 font-display text-sm font-semibold text-brand-300 transition-all duration-300 hover:bg-brand-500 hover:text-white"
            >
              <CalendarPlus className="h-4 w-4" /> Add to Calendar
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Countdown;
